import { FC, useRef, useEffect, useState, useCallback } from "react";
import { useDataTableStore } from "../../stores/DataTableStore";
import { useFormik } from "formik";
import * as Yup from 'yup';
import Swal from "sweetalert2";
import clsx from "clsx";
import useAccessToken from "../../hooks/AccessToken";
import { KTSVG } from "../../_metronic/helpers";
import { currencyFormatter, customThousandSeparatorDot, getDataById, handleCaps, isValidUrl } from "../../functions/general";
import Select from "react-select"
import { nanoid } from "@reduxjs/toolkit";
import useSWR from "swr";
import axios from "axios";
import { Currency, ExpenseCategory } from "../../interfaces/Reimbursement";
import { DatePicker } from "rsuite";
import { insertReimbursement } from "../../api/ReimbursementCRUD";
import moment from "moment-timezone";
import { insertAttachment, insertMultiAttachment } from "../../api/ResourceAPI";
import { useIntl } from "react-intl";
import useTodayRange from "../../hooks/TodayRange";
import useHandleCloseModal from "../../hooks/HandleCloseModal";
import InputAttachment from "../Input/InputAttachment";
import { Meeting } from "../../interfaces/Meeting";
import { Merchant } from "../../interfaces/Merchant";
import VirtualizedSelect from "../Select/SelectVirtualized";
import useUser from "../../hooks/User";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { isAllowAddReimburse, isAllowApproveReimburse, isAllowUploadPaymentReimburse } from "../../functions/reimbursement";
import { MAX_FILE_SIZE } from "../../constant/General";
import { useSettingsStore } from "../../stores/SettingsStore";
import { useReimbursementStore } from "../../stores/ReimbursementStore";
import { CustomField } from "../../interfaces/Settings";
import RenderCustomFieldGeneralForm from "../RenderComponent/RenderCustomFieldGeneralForm";
import { useMerchantStore } from "../../stores/MerchantStore";
import { usePipelineStore } from "../../stores/PipelineStore";
import { useMeetingStore } from "../../stores/MeetingsStore";
import { useProductStore } from "../../stores/ProductStore";
import { useUserStore } from "../../stores/UserStore";

interface SelectOption {
    value: string
    label: string
}

interface Props {
    meeting?: Meeting
    merchant?: Merchant
}

const AddReimbursement: FC<Props> = ({ meeting, merchant }) => {
    const todayRange = useTodayRange();
    const { settings } = useSettingsStore()
    const { remainingLimitMonth, remainingLimitYear, selectMultiReimbursement } = useReimbursementStore()
    const { simpleMerchants } = useMerchantStore()
    const { simplePipelines } = usePipelineStore()
    const { simpleMeetings } = useMeetingStore()
    const { simpleProducts } = useProductStore()
    const { users } = useUserStore()
    const refArray = useRef<(HTMLInputElement | null)[]>([]);
    const API_URL = process.env.REACT_APP_API_URL
    const [modalBtnLoading, setModalBtnLoading] = useState(false)
    const modalRef = useRef<HTMLDivElement | null>(null);
    const closeModalRef = useRef<HTMLButtonElement>(null);
    const { tablesData, setTablesData } = useDataTableStore()
    const token = useAccessToken()
    const user = useUser()
    const [categoryData, setCategoryData] = useState<SelectOption[]>([]);
    const [currencyData, setCurrencyData] = useState<SelectOption[]>([]);
    const [currencyType, setCurrencyType] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<any>([]);
    const intl = useIntl()
    const [customFields, setCustomFields] = useState<CustomField>({})

    const fetcher = (url: string) => axios.get(url, {
        headers: {
            'X-Auth-token': token
        }
    }).then(res => res.data.data)
    const { data: categories = [], error: categoriesError, isLoading: categoriesLoading } = useSWR(`${API_URL}reimbursement-categories`, fetcher)
    const { data: currencies = [], error: currenciesError, isLoading: currenciesLoading } = useSWR(`${API_URL}currencies`, fetcher)

    const handleNominalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = currencyFormatter(e.target.value, currencyType.toString() === '840' ? false : true, currencyType.toString() === '840' ? 'USD' : 'IDR');
        formik.setFieldValue('nominal', formattedValue, false);
    };

    const formSchema = Object.keys(customFields).reduce((obj: any, item: string) => {
        const field = customFields[item];
        const isRequiredMessage = intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: field.name });
        const isValidUrlMessage = intl.formatMessage({ id: "FORM.VALIDATION.MUST_LINK" });

        if (field.type === 'multiple' || field.type === 'multipleOption' || field.type === 'images' || field.type === 'files' || field.type === 'videos' || field.type === 'merchant' || field.type === 'user' || field.type === 'meeting' || field.type === 'product') {
            obj[item] = field.required
                ? Yup.array().min(1, isRequiredMessage).required(isRequiredMessage).nullable()
                : Yup.array().notRequired().nullable();
        } else if (field.type === 'date') {
            obj[item] = field.required
                ? Yup.string().required(isRequiredMessage).nullable()
                : Yup.string().notRequired().nullable();
        } else if (field.type === 'link') {
            obj[item] = field.required
                ? Yup.string().nullable().required(isRequiredMessage).test('is-required', isValidUrlMessage, function (value) {
                    return !value || isValidUrl(value);
                })
                : Yup.string().nullable().test('is-required', isValidUrlMessage, function (value) {
                    return !value || isValidUrl(value);
                });
        } else if (field.type === 'dateTimeRange') {
            if (field.required) {
                obj[item] = Yup.array().min(1, isRequiredMessage).required(isRequiredMessage).nullable();
            } else {
                obj[item] = Yup.array().notRequired().nullable();
            }
        } else if (field.type === 'email') {
            obj[item] = field.required
                ? Yup.string().email(intl.formatMessage({ id: "FORM.VALIDATION.EMAIL" })).required(isRequiredMessage).nullable()
                : Yup.string().email(intl.formatMessage({ id: "FORM.VALIDATION.EMAIL" })).notRequired().nullable();
        } else if (field.type === 'serial') {
            obj[item] = Yup.array().notRequired().nullable();
        }
        else {
            obj[item] = field.required
                ? Yup.string().required(isRequiredMessage).nullable()
                : Yup.string().notRequired().nullable();
        }

        return obj;
    }, {});

    const formik = useFormik({
        initialValues: {
            name: '',
            category: '',
            dateSpend: '',
            currency: '',
            nominal: '',
            description: '',
            meetingId: meeting?.id || '',
            merchantId: merchant?.id || '',
            attachment: [],
            ...Object.fromEntries(Object.keys(customFields).map((key) => [key, null])),
        },
        validationSchema: Yup.object().shape({
            name: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.NAME" }) })),
            category: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.CATEGORY" }) })),
            nominal: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.EXPENSE_NOMINAL" }) })),
            dateSpend: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.EXPENSE_DATE" }) })).nullable(),
            currency: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.CURRENCY" }) })),
            attachment: Yup.array().min(1, intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PROOF_OF_EXPENSE" }) })).required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PROOF_OF_EXPENSE" }) })).nullable(),
            description: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.DESCRIPTION" }) })),
            ...formSchema
        }),
        enableReinitialize: true,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            setModalBtnLoading(true)
            setSubmitting(true)

            var referenceType = values.meetingId ? "MEETING" : values.merchantId ? "MERCHANT" : "NONE"
            var referenceId = values.meetingId ? values.meetingId : values.merchantId ? values.merchantId : null

            const customFieldsValues: Record<string, string | string[] | number | null> = Object.keys(customFields).reduce(
                (obj, item) => ({
                    ...obj,
                    [item]: (values as Record<string, string | string[] | number | null>)[item],
                }),
                {}
            );

            const convertCustomFields: Record<string, any> = Object.keys(customFields).reduce(
                (obj, item) => {
                    if (!obj.info_custom_fields) {
                        obj.info_custom_fields = {};
                    }

                    if (customFields[item].type === 'merchant') {
                        const selectedMerchants = getDataById(values[item as keyof typeof values] as any, simpleMerchants);
                        obj[`custom_fields.${item}`] = selectedMerchants?.map((merchant: any) => merchant.name).join(", ");
                        obj.info_custom_fields[item] = selectedMerchants?.map((merchant: any) => ({
                            merchant_template_id: merchant.merchantTemplateId,
                            merchant_id: merchant.id,
                            merchant_name: merchant.name
                        })) || [];
                    }
                    else if (customFields[item].type === 'user') {
                        const selectedUsers = getDataById(values[item as keyof typeof values] as any, users);
                        obj[`custom_fields.${item}`] = selectedUsers?.map((user: any) => user.name).join(", ");
                        obj.info_custom_fields[item] = selectedUsers?.map((user: any) => ({
                            id: user.id,
                            name: user.name
                        })) || [];
                    }
                    else if (customFields[item].type === 'pipeline') {
                        const selectedPipeline = getDataById(values[item as keyof typeof values] as string, simplePipelines);
                        obj[`custom_fields.${item}`] = selectedPipeline ?
                            `${selectedPipeline.name} / ${currencyFormatter(selectedPipeline.nominal, user?.data?.companyCurrencyNeedRound)} / ${moment(selectedPipeline.dateCreated).format('DD MMM YYYY')}`
                            : '-';
                        obj.info_custom_fields[item] = selectedPipeline ? [{
                            id: selectedPipeline.id,
                            name: selectedPipeline.name,
                            nominal: selectedPipeline.nominal,
                            dateCreated: selectedPipeline.dateCreated
                        }] : [];
                    }
                    else if (customFields[item].type === 'meeting') {
                        const selectedMeeting: any[] = getDataById(values[item as keyof typeof values] as string, simpleMeetings);
                        obj[`custom_fields.${item}`] = selectedMeeting && selectedMeeting.length > 0 ?
                            selectedMeeting.map((meeting: any) => `${meeting.name} / ${moment(meeting.dateMeet).format('DD MMM YYYY')} / ${meeting.meetingStatus}`).join(', ')
                            : '-';
                        obj.info_custom_fields[item] = selectedMeeting && selectedMeeting.length > 0 ? selectedMeeting.map((meeting: any) => ({
                            meeting_template_id: meeting.meetingTemplateId,
                            id: meeting.id,
                            merchant_name: meeting.name,
                            date_meet: meeting.dateMeet,
                            meeting_status: meeting.meetingStatus
                        })) : [];
                    } else if (customFields[item].type === 'product') {
                        const selectedProducts = getDataById(values[item as keyof typeof values] as any, simpleProducts);
                        obj[`custom_fields.${item}`] = selectedProducts?.map((product: any) => product.name).join(", ");
                        obj.info_custom_fields[item] = selectedProducts?.map((product: any) => ({
                            product_id: product.id,
                            product_name: product.name,
                            product_prices: product.product_prices,
                        })) || [];
                    }
                    else {
                        obj[`custom_fields.${item}`] = values[item as keyof typeof values] || '';
                        obj.info_custom_fields[item] = [];
                    }

                    return obj;
                },
                {} as Record<string, any>
            );

            // Use Promise.all to await all async operations
            var countResourceError = 0
            await Promise.all(
                Object.keys(customFields).map(async (key) => {
                    if (customFields[key].type === 'image' || customFields[key].type === 'file' || customFields[key].type === 'video') {
                        try {
                            if (values[key as keyof typeof values]) {
                                const resourceResponse = await insertAttachment(values[key as keyof typeof values] as string, 'PIPELINE', token);
                                if (resourceResponse.data.success) customFieldsValues[key] = resourceResponse.data.data.length > 0 ? resourceResponse.data.data[0] : null;
                                else countResourceError++
                            }
                        } catch (error) {
                            countResourceError++
                        }
                    } else if (customFields[key].type === 'images' || customFields[key].type === 'files' || customFields[key].type === 'videos') {
                        try {
                            if (values[key as keyof typeof values]) {
                                const resourceResponse = await insertMultiAttachment(values[key as keyof typeof values] as any, 'PIPELINE', token);
                                if (resourceResponse.data.success) customFieldsValues[key] = resourceResponse.data.data.length > 0 ? resourceResponse.data.data : null;
                                else countResourceError++
                            }
                        } catch (error) {
                            countResourceError++
                        }
                    } else if (customFields[key].type === 'invoice_reminder') {
                        try {
                            const resourceResponse = await insertAttachment(values[key + "_invoice_url" as keyof typeof values] as string, 'PIPELINE', token);
                            if (resourceResponse.data.success) customFieldsValues[key] = JSON.stringify({ date_due: values[key + "_date_due" as keyof typeof values], invoice_url: resourceResponse.data.data.length > 0 ? resourceResponse.data.data[0] : null })
                            else countResourceError++
                        } catch (error) {
                            countResourceError++
                        }
                    } else if (customFields[key].type === 'multiple' || customFields[key].type === 'multipleOption') {
                        var multipleValues = values[key as keyof typeof values]?.length! > 0 ? values[key as keyof typeof values] : null;
                        customFieldsValues[key] = multipleValues as string
                    } else if (customFields[key].type === 'dateTimeRange') {
                        if (values[key as keyof typeof values]) {
                            const [startDate, endDate] = values[key as keyof typeof values];
                            customFieldsValues[key] = [
                                startDate ? (new Date(startDate).toISOString() as any) : null,
                                endDate ? (new Date(endDate).toISOString() as any) : null
                            ];
                        } else {
                            customFieldsValues[key] = null;
                        }
                    } else if (customFields[key].type === 'number') {
                        const stringValue = values[key as keyof typeof values] as string;
                        customFieldsValues[key] = stringValue ? customThousandSeparatorDot(stringValue as unknown as number) : null;
                    }
                })
            );

            if (countResourceError > 0) {
                Swal.fire({
                    icon: "error",
                    text: `${intl.formatMessage({ id: "FORM.VALIDATION.ADD_FAILED" })}.`,
                    heightAuto: false,
                    target: document.getElementById('add-pipeline-modal'),
                    confirmButtonText: 'Ok'
                })
                setModalBtnLoading(false);
                setSubmitting(false);
            } else {
                try {
                    let attachments: string[] = []
                    if (values.attachment.length > 0) {
                        for (let file of values.attachment) {
                            const resourceResponse = await insertAttachment(file, 'REIMBURSEMENT', token);
                            attachments.push(resourceResponse.data.data[0])
                        }
                    }

                    const response = await insertReimbursement(selectMultiReimbursement.id, values.name, values.description, parseInt(values.currency), moment(values.dateSpend).format('YYYY-MM-DD HH:mm:ss'), values.category, parseFloat((values.nominal).split(',').join('')), attachments, referenceType, referenceId, customFieldsValues, token)
                    if (response.data.success) {
                        Swal.fire({
                            icon: "success",
                            title: intl.formatMessage({ id: "ALERT.TITLE.SUCCESS" }),
                            text: `${intl.formatMessage({ id: "FORM.VALIDATION.ADD_SUCCESS" })}.`,
                            heightAuto: false,
                        })
                        closeModalRef.current?.click();

                        if (meeting) {
                            setTablesData('meetings', (tablesData.meetings.map(item => {
                                if (item.id === meeting.id) {
                                    return {
                                        ...item,
                                        reimbursements: [response.data.data, ...item.reimbursements]
                                    }
                                } else {
                                    return item
                                }
                            })));
                        }

                        if (merchant) {
                            setTablesData('merchants', (tablesData.merchants.map(item => {
                                if (item.id === merchant.id) {
                                    return {
                                        ...item,
                                        reimbursements: [response.data.data, ...item.reimbursements]
                                    }
                                } else {
                                    return item
                                }
                            })));
                        }

                        let key = isAllowApproveReimburse(user) || isAllowUploadPaymentReimburse(user) ? 'reimbursements' : 'my-reimbursements'
                        setTablesData(key, ([
                            ...tablesData[key],
                            {
                                ...response.data.data,
                                ...convertCustomFields['custom_fields'],
                                info_custom_fields: convertCustomFields.info_custom_fields,
                                customFields: customFieldsValues,
                                categoryName: response.data.data.reimbursementCategory.name
                            }
                        ]));
                    }
                } catch (error: any) {
                    Swal.fire({
                        icon: 'error',
                        title: error.response.data.message,
                        confirmButtonText: 'Ok',
                        heightAuto: false
                    })
                } finally {
                    setModalBtnLoading(false)
                    setSubmitting(false)
                }
            }
        }
    });

    const handlePaste = useCallback(async (event: ClipboardEvent) => {
        const target = event.target as HTMLElement;
        const tagName = target.tagName.toLowerCase();
        const isEditable = target.isContentEditable || tagName === "input" || tagName === "textarea";

        if (isEditable) {
            // Biarkan paste berjalan normal di input/textarea/contenteditable
            return;
        }

        // Kalau bukan input/textarea/contenteditable, tangani paste file
        event.preventDefault();

        const items = event.clipboardData?.items;
        if (!items) return;

        const itemsArray = Array.from(items);
        const fileItems = itemsArray.filter(item => item.kind === "file");

        if (fileItems.length > 0) {
            const filePromises = fileItems.map((file) =>
                new Promise<File | null>((resolve) => {
                    const blob = file.getAsFile();
                    if (!blob) return resolve(null);

                    if (file.type.startsWith("image/")) {
                        if (blob.size > MAX_FILE_SIZE) {
                            Swal.fire({
                                icon: "error",
                                title: "Image size exceeds 5MB limit",
                                text: "Please use an image smaller than 5MB.",
                                confirmButtonText: "Ok",
                            });
                            return resolve(null);
                        }
                    }
                    resolve(blob);
                })
            );

            const results = await Promise.all(filePromises);
            const validFiles = results.filter((file): file is File => file !== null);

            if (validFiles.length > 0) {
                const currentAttachments = formik.values.attachment || [];
                formik.setFieldValue("attachment", [...currentAttachments, ...validFiles], false);
            }
        }
    }, [formik]);


    useEffect(() => {
        // Get the modal element
        const modalElement = modalRef.current;

        // Define the event listener
        const handleModalHidden = () => {
            formik.resetForm()
            setFile([])
            fileInputRef.current?.value && (fileInputRef.current.value = "")
            formik.setFieldValue('dateSpend', '', false)

            Object.keys(customFields).map((key, index) => {
                // Reset 
                const ref = refArray.current[index];
                if (ref && ref.value) {
                    ref.value = "";
                }

                if (customFields[key].type === "multiple") {
                    var checkboxes = document.getElementsByName(key)
                    checkboxes.forEach((checkbox: any) => {
                        checkbox.checked = false
                    })
                }

                if (customFields[key].type === "date") {
                    formik.setFieldValue(key, null, false)
                }
            });
        };

        // Attach the event listener
        modalElement?.addEventListener('hidden.bs.modal', handleModalHidden);

        // Cleanup
        return () => {
            modalElement?.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
    }, [formik]);

    useEffect(() => {
        if (!categoriesLoading) {
            const categoryData = categories.map((category: ExpenseCategory) => ({
                value: category.id,
                label: category.name
            }));

            // Prepend the two objects to the salesData array
            setCategoryData(categoryData);
        }
    }, [categories]);

    useEffect(() => {
        if (!currenciesLoading) {
            let sortCurrencies = currencies

            sortCurrencies.forEach(function (currency: Currency, index: number) {
                if (currency.code === "IDR" && currency.name === "Rupiah") {
                    sortCurrencies.splice(index, 1);
                    sortCurrencies.unshift(currency);
                }
            });

            const currencyData = sortCurrencies.map((currency: Currency) => ({
                value: currency.id,
                label: currency.code + ' - ' + currency.name + (currency.symbol ? (' (' + currency.symbol + ')') : '')
            }));

            // Prepend the two objects to the salesData array
            setCurrencyData(currencyData);
        }
    }, [currencies]);

    useEffect(() => {
        document.addEventListener('paste', handlePaste);
        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [handlePaste]);

    useEffect(() => {
        if (Object.keys(selectMultiReimbursement).length !== 0) {
            var data = selectMultiReimbursement.customFields
            setCustomFields(data)
        }
    }, [selectMultiReimbursement])

    const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);

    return (
        <div>
            {
                !meeting && !merchant &&
                <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-labels" hidden={isAllowAddReimburse(remainingLimitYear, remainingLimitMonth)}>{intl.formatMessage({ id: "FORM.LABEL.LIMIT_REACHED" })}</Tooltip>} >
                    <div style={{ display: 'inline-block', cursor: isAllowAddReimburse(remainingLimitYear, remainingLimitMonth) ? 'pointer' : 'not-allowed' }} >
                        <button
                            type="button"
                            className="btn btn-primary mt-3"
                            disabled={!isAllowAddReimburse(remainingLimitYear, remainingLimitMonth)}
                            data-bs-toggle="modal"
                            data-bs-target={isAllowAddReimburse(remainingLimitYear, remainingLimitMonth) ? "#add-reimbursement-modal" : ""}
                            style={{ pointerEvents: 'auto' }}
                        >
                            <i className="fas fa-plus"></i>
                            <span>{intl.formatMessage({ id: 'FORM.ACTION.ADD' })} {selectMultiReimbursement.name}</span>
                        </button>
                    </div>
                </OverlayTrigger>
            }
            <div className="modal fade" tabIndex={-1} id="add-reimbursement-modal" ref={modalRef}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{intl.formatMessage({ id: 'FORM.ACTION.ADD' })} {selectMultiReimbursement.name}</h5>
                            <div
                                className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                                onClick={handleCloseModal}
                            >
                                <KTSVG
                                    path="/media/icons/duotune/arrows/arr061.svg"
                                    className="svg-icon svg-icon-2x"
                                />
                            </div>
                        </div>
                        <form onSubmit={formik.handleSubmit} noValidate>
                            <div className="modal-body">
                                <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.NAME' })}</label>
                                <div className="input-group mb-3">
                                    <input
                                        placeholder={intl.formatMessage({ id: 'FORM.LABEL.NAME' })}
                                        {...formik.getFieldProps('name')}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            { 'is-invalid': formik.touched.name && formik.errors.name },
                                        )}
                                        type='text'
                                        name='name'
                                        autoComplete='off'
                                        onInput={(e) => handleCaps(e)}
                                    />
                                </div>
                                {formik.touched.name && formik.errors.name && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.name}</span>
                                    </div>
                                )}
                                {/* Category */}
                                <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.CATEGORY' })}</label>
                                <div className="input-group mb-3">
                                    <VirtualizedSelect
                                        className={clsx("w-100")}
                                        options={categoryData}
                                        placeholder={intl.formatMessage({ id: 'FORM.ACTION.CHOOSE_CATEGORY' })}
                                        value={categoryData.find(option => option.value === formik.values.category) || null}
                                        onChange={
                                            option => formik.handleChange({
                                                target: {
                                                    name: "category",
                                                    value: option?.value || ""
                                                }
                                            })
                                        }
                                        onBlur={
                                            () => formik.values.category === "" && formik.setFieldTouched("category", true)
                                        }
                                        isDisabled={categoriesLoading}
                                    />
                                </div>
                                {formik.touched.category && formik.errors.category && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.category}</span>
                                    </div>
                                )}
                                {/* Date */}
                                <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.EXPENSE_DATE' })}</label>
                                <div className="input-group mb-3">
                                    <DatePicker
                                        oneTap
                                        ranges={todayRange}
                                        editable={false}
                                        format="dd MMM yyyy"
                                        value={formik.values.dateSpend ? new Date(formik.values.dateSpend) : null}
                                        style={{ width: "100%", zIndex: 0 }}
                                        container={document.querySelector('#add-reimbursement-modal') as HTMLElement}
                                        onChange={(value: any) => {
                                            if (value) formik.setFieldValue('dateSpend', value, false)
                                            else formik.setFieldValue('dateSpend', null, false)
                                        }}
                                        shouldDisableDate={(date) => {
                                            return date > new Date();
                                        }}
                                    />
                                </div>
                                {formik.touched.dateSpend && formik.errors.dateSpend && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.dateSpend}</span>
                                    </div>
                                )}
                                {/* Currency */}
                                <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.CURRENCY' })}</label>
                                <div className="input-group mb-3">
                                    <VirtualizedSelect
                                        className={clsx("w-100")}
                                        options={currencyData}
                                        placeholder={intl.formatMessage({ id: 'FORM.ACTION.CHOOSE_CURRENCY' })}
                                        value={currencyData.find(option => option.value === formik.values.currency) || null}
                                        onChange={
                                            (option) => {
                                                setCurrencyType(option?.value || "");
                                                formik.handleChange({
                                                    target: {
                                                        name: "currency",
                                                        value: option?.value || ""
                                                    }
                                                })
                                            }
                                        }
                                        onBlur={
                                            () => formik.values.currency === "" && formik.setFieldTouched("currency", true)
                                        }
                                        isDisabled={currenciesLoading}
                                    />
                                </div>
                                {formik.touched.currency && formik.errors.currency && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.currency}</span>
                                    </div>
                                )}
                                {/* Nominal */}
                                <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.EXPENSE_NOMINAL' })}</label>
                                <div className="input-group mb-3">
                                    <input
                                        placeholder={intl.formatMessage({ id: 'FORM.LABEL.EXPENSE_NOMINAL' })}
                                        value={formik.values.nominal}
                                        onChange={handleNominalChange}
                                        onBlur={formik.handleBlur}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            { 'is-invalid': formik.touched.nominal && formik.errors.nominal },
                                        )}
                                        type='text'
                                        name='nominal'
                                        autoComplete='off'
                                        style={{ zIndex: 0 }}
                                    />
                                </div>
                                {formik.touched.nominal && formik.errors.nominal && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.nominal}</span>
                                    </div>
                                )}
                                {/* Description */}
                                <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.DESCRIPTION' })}</label>
                                <div className="input-group mb-3">
                                    <textarea
                                        placeholder={intl.formatMessage({ id: 'FORM.LABEL.DESCRIPTION' })}
                                        id="floatingTextarea"
                                        {...formik.getFieldProps('description')}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            { 'is-invalid': formik.touched.description && formik.errors.description },
                                        )}
                                        style={{ zIndex: 0 }}
                                        name="description"
                                        maxLength={255}
                                        onInput={(e) => handleCaps(e)}
                                    >
                                    </textarea>
                                </div>
                                {formik.touched.description && formik.errors.description && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.description}</span>
                                    </div>
                                )}
                                <span className={`form-label fs-6 fw-bolder text-dark required`}>{intl.formatMessage({ id: 'FORM.LABEL.PROOF_OF_EXPENSE' })}</span><br />
                                <small><i>*Format PNG, JPG, JPEG</i></small>
                                <InputAttachment keyString={'attachment'} formik={formik} isMulti={true} type="input" accept="image/png, image/jpeg, image/jpg" />
                                {
                                    formik.touched.attachment && formik.errors.attachment &&
                                    (
                                        <div className='fv-plugins-message-container text-danger'>
                                            <span role='alert' className="text-danger">{formik.errors.attachment}</span>
                                        </div>
                                    )
                                }
                                {customFields &&
                                    <RenderCustomFieldGeneralForm
                                        customFieldGeneral={customFields}
                                        formik={formik}
                                        errors={formik.errors}
                                        containerRef={document.querySelector('#add-reimbursement-modal') as HTMLElement}
                                        typeAction="add"
                                    />
                                }
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light d-none"
                                    data-bs-dismiss="modal"
                                    ref={closeModalRef}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={handleCloseModal}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button type="submit" className={`btn btn-primary me-10 ${modalBtnLoading ? 'disabled' : ''}`} data-kt-indicator={modalBtnLoading ? 'on' : 'off'}>
                                    <span className="indicator-label">
                                        {intl.formatMessage({ id: 'FORM.ACTION.ADD' })}
                                    </span>
                                    <span className="indicator-progress">
                                        Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export { AddReimbursement }